import { mapState, mapActions, mapMutations, mapGetters } from 'vuex';
import { tsNow } from '@/utils/timeSync';
import logTransport from '@/mixins/logTransport';
import store from '@/store/index';

export default {
  mixins: [logTransport],
  data() {
    return {
      intervalCount: 0,
      timeout: 0, // request timeout in ms
      timeoutBuffer: 3000, // default timeout buffer in ms
      maxRetries: 5, // max attempt for requesting token
      tokenResponse: null,
    };
  },
  computed: {
    ...mapState(['issuedAt', 'lessonId', 'lessonStart', 'lessonStartWarmUp', 'isDevTest']),
    ...mapGetters([
      'isWaitingForLesson',
      'isLessonOngoing',
      'isWarmUpPeriod',
      'isWaitingOrWarmUpPeriod',
      'isStudent',
      'isAmazonChime'
    ]),

    maxRetriesReached() {
      return this.intervalCount + 1 >= this.maxRetries;
    }
  },
  watch: {
    maxRetriesReached: {
      handler: function () {
        if (this.maxRetriesReached && this.isBeyondLessonStartTime()) {
          this.addLogTransport();
          this.$logger.error('Maximum request token retries has been reached', {
            operation: 'token:request',
            event_type: 20,
            lesson_id: this.lessonId
          });
          this.removeLogTransport();
          this.hasConnectionProblem();
        }
      },
    },
    isLessonOngoing: function (value) {
      if (value) {
        this.setIsLessonBecameOngoing(true);
        this.setConnected(true);
      }
    },
  },
  methods: {
    ...mapActions(['getLessonInfo', 'getDevTestLessonInfo']),
    ...mapActions('connection', ['hasConnectionProblem']),
    ...mapMutations('lesson', ['setIsLessonBecameOngoing']),
    ...mapMutations('socketio', ['setConnected']),

    /** 
     * Adds a randomized delay before requesting an AWS Chime lesson token
     */
    async waitChimeDelay() {
      if (!this.isAmazonChime) return;

      const min = 0;
      const max = 2000;

      const ms = Math.floor(Math.random() * (max - min + 1)) + min;
      this.$logger.debug(`[Token Stagger] Waiting for ${ms}ms before requesting token`);
      await new Promise(resolve => setTimeout(resolve, ms));
    },

    // Request new lesson token when warm period has been reached
    async renewTokenOnWarmUpPeriod() {
      if (this.isWarmUpPeriod) return;

      if (this.isBeyondLessonStartTime()) {
        this.renewTokenOnLessonStart();
        return;
      }

      let warmUpInterval = setInterval(async () => {
        if (this.pendingWarmUpStatus()) {
          clearInterval(warmUpInterval);

          await this.requestLessonToken();
          this.renewTokenOnLessonStart();
        } else if (this.isBeyondLessonStartTime() && !this.isLessonOngoing) {
          // Warmup window was missed (e.g., component mounted in the last second
          // before lesson start). Fall through directly to lesson-start renewal.
          clearInterval(warmUpInterval);
          this.renewTokenOnLessonStart();
        }
      }, 1000);
    },

    // Rerequest lesson token on api when reaches the lesson start time
    renewTokenOnLessonStart() {
      if (this.isLessonOngoing) return;

      let lessonStartInterval = setInterval(() => {
        if (this.isBeyondLessonStartTime()) {
          this.requestLessonToken();
          this.intervalCount = 0;

          clearInterval(lessonStartInterval);
        }
      }, 1000);
    },

    // Check if sync time is equal or beyond the lesson start time
    isBeyondLessonStartTime() {
      return tsNow() >= this.lessonStart * 1000;
    },

    // Check if the sync time is equal to or beyond the warmup period
    isBeyondWarmUpPeriod() {
      return tsNow() >= this.lessonStartWarmUp * 1000;
    },

    pendingWarmUpStatus() {
      return this.isBeyondWarmUpPeriod() && !this.isBeyondLessonStartTime() && !this.isWarmUpPeriod;
    },

    pendingOngoingStatus() {
      return this.isBeyondLessonStartTime() && !this.isLessonOngoing;
    },

    // Request lesson token
    async requestLessonToken() {
      await this.waitChimeDelay();

      if (this.isDevTest) {
        await this.getDevTestLessonInfo();
        return;
      }

      this.tokenResponse = await this.getLessonInfo({ lessonId: this.lessonId, legacy: store.getters.isLegacy });
      this.checkLessonStatus();
    },

    // Check if lesson status is still `waiting` and if maximum attempt was reached
    checkLessonStatus() {
      if (this.maxRetriesReached || (!this.pendingWarmUpStatus() && !this.pendingOngoingStatus())) {
        return;
      }

      this.addLogTransport();
      this.$logger.debug('Lesson token response', {
        payload: JSON.stringify(this.tokenResponse && this.tokenResponse.message),
      });
      this.removeLogTransport();

      // Get the time difference between server time and issued time of jwt
      const nextRequest = this.isWarmUpPeriod ? this.lessonStart : this.lessonStartWarmUp;
      const timeDifference = nextRequest - this.issuedAt;
      this.timeout = Math.max(0, timeDifference) + this.timeoutBuffer;

      this.retryRequest();
    },

    // Retry request until condition has been met
    retryRequest() {
      setTimeout(() => {
        this.intervalCount++;
        this.requestLessonToken();
      }, this.timeout);
    }
  }
};
